import { useEffect, useRef, useState } from 'react';

import {
  Anchor,
  Box,
  Card,
  Checkbox,
  Label,
  LabeledControl,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import type { PhotoProps } from './PhotoDetailsModal';

const AB_723_URL = 'https://leginfo.legislature.ca.gov/faces/billNavClient.xhtml?bill_id=202520260AB723';

type OnComplete = (errorMessage?: string) => void;

type FocusTarget = 'checkbox' | 'add' | 'replace' | 'unmark';

type Props = {
  photo: PhotoProps;
  isDisabled: boolean;
  onSavingChange: (isSaving: boolean) => void;
  getOriginalPhotoUrl: (photoKey: string) => string;
  onDigitallyAlteredChange?: (args: { photoId: string; isDigitallyAltered: boolean; onComplete: () => void }) => void;
  onOriginalPhotoUpload?: (args: { photoId: string; file: File; onComplete: OnComplete }) => void;
  onOriginalPhotoRemove?: (args: { photoId: string; onComplete: OnComplete }) => void;
};

export const DigitallyAlteredPhotoSection = ({
  photo,
  isDisabled,
  onSavingChange,
  getOriginalPhotoUrl,
  onDigitallyAlteredChange,
  onOriginalPhotoUpload,
  onOriginalPhotoRemove,
}: Props) => {
  const [isOriginalPhotoSaving, setIsOriginalPhotoSaving] = useState(false);
  const [originalPhotoError, setOriginalPhotoError] = useState<string | null>(null);
  const [isConfirmingUncheck, setIsConfirmingUncheck] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const originalPhotoInputRef = useRef<HTMLInputElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const replaceButtonRef = useRef<HTMLButtonElement>(null);
  const unmarkButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!focusTarget) {
      return;
    }
    const refs = { checkbox: checkboxRef, add: addButtonRef, replace: replaceButtonRef, unmark: unmarkButtonRef };
    refs[focusTarget].current?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const openFilePicker = () => {
    if (!isDisabled) {
      originalPhotoInputRef.current?.click();
    }
  };

  const changeDigitallyAltered = (isDigitallyAltered: boolean) => {
    if (!onDigitallyAlteredChange) {
      return;
    }
    setIsConfirmingUncheck(false);
    setFocusTarget('checkbox');
    onSavingChange(true);
    onDigitallyAlteredChange({
      photoId: photo.id,
      isDigitallyAltered,
      onComplete: () => onSavingChange(false),
    });
  };

  const saveOriginalPhoto = (
    messages: { pending: string; success: string; focusOnSuccess: FocusTarget },
    save: (onComplete: OnComplete) => void,
  ) => {
    setOriginalPhotoError(null);
    setStatusMessage(messages.pending);
    setIsOriginalPhotoSaving(true);
    onSavingChange(true);
    save((errorMessage) => {
      setIsOriginalPhotoSaving(false);
      onSavingChange(false);
      if (errorMessage) {
        setStatusMessage('');
        setOriginalPhotoError(errorMessage);
        return;
      }
      setStatusMessage(messages.success);
      setFocusTarget(messages.focusOnSuccess);
    });
  };

  const renderOriginalPhotoRow = () => {
    if (isConfirmingUncheck) {
      return (
        <Box role="alert" data-testid="PhotoDetailsModal-ConfirmUncheck">
          <Paragraph css={{ textStyle: 'body-sm' }}>Unmarking also removes the original photo.</Paragraph>
          <Box css={{ display: 'flex', gap: 'default', marginTop: 'tightest' }}>
            <TextButton
              ref={unmarkButtonRef}
              tone="critical"
              aria-disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  changeDigitallyAltered(false);
                }
              }}
            >
              Unmark and remove
            </TextButton>
            <TextButton
              aria-disabled={isDisabled}
              onClick={() => {
                if (isDisabled) {
                  return;
                }
                setIsConfirmingUncheck(false);
                setFocusTarget('checkbox');
              }}
            >
              Keep marked
            </TextButton>
          </Box>
        </Box>
      );
    }

    if (photo.originalPhotoKey) {
      return (
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tight' }}>
          <Box
            aria-hidden
            data-testid="PhotoDetailsModal-OriginalPhotoThumbnail"
            css={{
              flex: '0 0 25%',
              aspectRatio: '4/3',
              backgroundColor: 'bg.accent.gray.hero',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: 'obj.default',
            }}
            style={{ backgroundImage: `url(${getOriginalPhotoUrl(photo.originalPhotoKey)})` }}
          />
          <Box>
            <Text textStyle="body-bold">{isOriginalPhotoSaving ? 'Saving…' : 'Original attached'}</Text>
            <Box css={{ display: 'flex', gap: 'default' }}>
              <TextButton
                ref={replaceButtonRef}
                aria-label="Replace original photo"
                aria-disabled={isDisabled}
                onClick={openFilePicker}
              >
                Replace
              </TextButton>
              <TextButton
                aria-label="Remove original photo"
                aria-disabled={isDisabled}
                onClick={() => {
                  if (isDisabled || !onOriginalPhotoRemove) {
                    return;
                  }
                  saveOriginalPhoto(
                    {
                      pending: 'Removing original photo',
                      success: 'Original photo removed',
                      focusOnSuccess: 'add',
                    },
                    (onComplete) => onOriginalPhotoRemove({ photoId: photo.id, onComplete }),
                  );
                }}
              >
                Remove
              </TextButton>
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <TextButton ref={addButtonRef} aria-disabled={isDisabled} onClick={openFilePicker}>
          {isOriginalPhotoSaving ? 'Uploading…' : 'Add original photo'}
        </TextButton>
        <Paragraph css={{ textStyle: 'body-sm', color: 'text.subtle' }}>
          The original will be available for renters to view alongside this listing photo.
        </Paragraph>
      </>
    );
  };

  return (
    <Card
      data-testid="PhotoDetailsModal-DigitallyAltered"
      elevated={false}
      css={{ marginBottom: '400', paddingBlockStart: 'tightest' }}
    >
      <LabeledControl
        controlId={`digitally-altered-${photo.id}`}
        label={<Label>Digitally altered photo</Label>}
        control={
          <Checkbox
            ref={checkboxRef}
            checked={Boolean(photo.isDigitallyAltered)}
            aria-disabled={isDisabled}
            onChange={() => {
              if (isDisabled) {
                return;
              }
              if (photo.isDigitallyAltered && photo.originalPhotoKey) {
                setIsConfirmingUncheck(true);
                setFocusTarget('unmark');
                return;
              }
              changeDigitallyAltered(!photo.isDigitallyAltered);
            }}
          />
        }
      />
      <Paragraph css={{ textStyle: 'body-sm', color: 'text.subtle' }}>
        Required in California (AB 723) if this photo was altered.{' '}
        <Anchor href={AB_723_URL} target="_blank" rel="noopener noreferrer">
          Read the law
        </Anchor>
      </Paragraph>

      {photo.isDigitallyAltered && (
        <Box
          data-testid="PhotoDetailsModal-OriginalPhoto"
          css={{
            marginTop: 'default',
            paddingTop: 'default',
            borderTopWidth: 'default',
            borderTopStyle: 'solid',
            borderTopColor: 'border.muted',
          }}
        >
          <input
            ref={originalPhotoInputRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="PhotoDetailsModal-OriginalPhotoInput"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file || !onOriginalPhotoUpload) {
                return;
              }
              saveOriginalPhoto(
                { pending: 'Uploading original photo', success: 'Original photo uploaded', focusOnSuccess: 'replace' },
                (onComplete) => onOriginalPhotoUpload({ photoId: photo.id, file, onComplete }),
              );
            }}
          />

          {renderOriginalPhotoRow()}

          {originalPhotoError && (
            <Paragraph
              role="alert"
              css={{ marginTop: 'tight', textStyle: 'body-sm', color: 'text.feedback.critical.hero' }}
            >
              {originalPhotoError}
            </Paragraph>
          )}
        </Box>
      )}

      <Box aria-live="polite" css={{ srOnly: true }}>
        {statusMessage}
      </Box>
    </Card>
  );
};
